/**
 * Live sales feeds: one ordered list per feed, merged on the server, pushed
 * as it changes.
 *
 * The API merges this marketplace's fills, every venue's confirmed fills and
 * the mempool's pending fills into one tape (`/collection/:slug/feed`,
 * `/inscriptions/activity/feed`) and streams its changes
 * (`.../feed/stream`): a `snapshot` of the head, then `delta` events with
 * `added` rows, `updated` rows (a pending sale confirming) and `removed`
 * keys (a pending sale that was dropped). Rows arrive already ordered and
 * deduplicated; this store only applies changes and pages back.
 *
 * One stream per feed however many components read it, closed a few
 * seconds after the last reader leaves so a re-render does not reconnect.
 * When the stream cannot connect the store polls the head instead and
 * keeps retrying the stream.
 */

import { API_URL } from '@/utils/constants'

export interface FeedRow {
	/** Stable identity: `tx:<settlement txid>:<item>` where known. */
	key: string
	/** `ow` = this marketplace, `global` = another venue, `mempool` = seen unconfirmed. */
	source: 'ow' | 'global' | 'mempool'
	status: 'pending' | 'confirmed'
	/** Sale time, ms: bought, block time, or first seen in the mempool. */
	ts: number
	txid?: string
	block_height?: number
	price_sats?: number
	marketplace?: number
	[field: string]: any
}

export interface FeedState {
	rows: readonly FeedRow[]
	/** Keys added by the stream in the last few seconds, for a highlight. */
	fresh: ReadonlySet<string>
	version: number
	hasMore: boolean
	isLoading: boolean
	isLoadingMore: boolean
	error: unknown
	transport: 'idle' | 'live' | 'polling'
}

interface HeadBody {
	version?: number
	rows?: FeedRow[]
	next?: string | null
}

interface DeltaBody {
	prev: number
	version: number
	added: FeedRow[]
	updated: FeedRow[]
	removed: string[]
}

const RECONNECT_MIN_MS = 1_000
const RECONNECT_MAX_MS = 30_000
const POLL_MS = 5_000
const RELEASE_GRACE_MS = 5_000
const FRESH_MS = 2_500

const EMPTY: FeedState = {
	rows: [],
	fresh: new Set(),
	version: 0,
	hasMore: false,
	isLoading: true,
	isLoadingMore: false,
	error: null,
	transport: 'idle'
}

/**
 * Apply one delta. The API orders the tape; this never sorts. `added` rows
 * are news and go on top in the order sent; `updated` rows (a pending sale
 * confirming, a correction) change in place; `removed` rows (a pending sale
 * that was dropped) leave. A row the reader has seen never moves.
 */
export function applyDelta(rows: readonly FeedRow[], d: DeltaBody): FeedRow[] {
	const removed = new Set(d.removed)
	const updated = new Map(d.updated.map((r) => [r.key, r]))
	const added = d.added.filter((r) => !rows.some((x) => x.key === r.key))
	const kept = rows.filter((r) => !removed.has(r.key)).map((r) => updated.get(r.key) ?? r)
	return [...added, ...kept]
}

/**
 * Fold a fresh snapshot of the head into what the reader already sees,
 * without moving anything they have seen: rows they hold change in place
 * (a pending sale confirming) or leave (a pending sale that was dropped);
 * rows new to them go on top in the API's order, but only if they are
 * newer than everything held -- anything older arrives through paging, never
 * in the middle of the list. The API's own order wins only for a reader
 * who holds nothing yet.
 */
export function mergeSnapshot(held: readonly FeedRow[], head: readonly FeedRow[]): FeedRow[] {
	if (held.length === 0) return [...head]
	const order = (r: FeedRow) => (typeof r.seen === 'number' ? r.seen : r.ts)
	const byKey = new Map(head.map((r) => [r.key, r]))
	const heldKeys = new Set(held.map((r) => r.key))
	const top = held.reduce((max, r) => Math.max(max, order(r)), -Infinity)
	const news = head.filter((r) => !heldKeys.has(r.key) && order(r) > top)
	const kept = held.flatMap((r) => {
		const fresh = byKey.get(r.key)
		if (fresh) return [fresh]
		return r.status === 'pending' ? [] : [r]
	})
	return [...news, ...kept]
}

interface Feed {
	path: string
	state: FeedState
	listeners: Set<() => void>
	next: string | null
	source: EventSource | null
	reconnectDelay: number
	reconnectTimer: number | null
	pollTimer: number | null
	releaseTimer: number | null
	freshTimer: number | null
	freshAt: Map<string, number>
	failures: number
}

const feeds = new Map<string, Feed>()

function emit(feed: Feed, patch: Partial<FeedState>) {
	feed.state = { ...feed.state, ...patch }
	for (const l of feed.listeners) l()
}

function markFresh(feed: Feed, keys: string[]) {
	if (keys.length === 0) return
	const now = Date.now()
	for (const k of keys) feed.freshAt.set(k, now)
	emit(feed, { fresh: new Set(feed.freshAt.keys()) })
	if (feed.freshTimer === null) {
		feed.freshTimer = window.setInterval(() => {
			const cutoff = Date.now() - FRESH_MS
			for (const [k, t] of feed.freshAt) if (t < cutoff) feed.freshAt.delete(k)
			emit(feed, { fresh: new Set(feed.freshAt.keys()) })
			if (feed.freshAt.size === 0 && feed.freshTimer !== null) {
				window.clearInterval(feed.freshTimer)
				feed.freshTimer = null
			}
		}, 500)
	}
}

function applyHead(feed: Feed, body: HeadBody) {
	const head = Array.isArray(body.rows) ? body.rows : []
	// Version 0 is the server saying it has no complete head yet (a cold
	// collection). Keep loading rather than paint an empty or partial list.
	if (!body.version) {
		scheduleHeadRetry(feed)
		return
	}
	// A stale poll must not rewind a newer streamed head.
	if (typeof body.version === 'number' && body.version < feed.state.version) return
	// The first page's cursor only matters until the reader pages back.
	if (feed.next === null || feed.state.rows.length <= head.length) feed.next = body.next ?? null
	emit(feed, {
		rows: mergeSnapshot(feed.state.rows, head),
		version: body.version ?? feed.state.version,
		hasMore: feed.next !== null,
		isLoading: false,
		error: null
	})
}

const headRetries = new WeakMap<Feed, number>()

function scheduleHeadRetry(feed: Feed) {
	const n = headRetries.get(feed) ?? 0
	if (n >= 6 || feed.state.rows.length > 0) {
		if (feed.state.rows.length === 0) emit(feed, { isLoading: false })
		return
	}
	headRetries.set(feed, n + 1)
	window.setTimeout(() => {
		if (feeds.get(feed.path) === feed && feed.state.rows.length === 0) void fetchHead(feed)
	}, 750 * 2 ** n)
}

async function fetchHead(feed: Feed) {
	try {
		const res = await fetch(`${API_URL}/${feed.path}`, { cache: 'no-store' })
		if (!res.ok) throw new Error(`feed ${res.status}`)
		applyHead(feed, await res.json())
	} catch (error) {
		if (feed.state.rows.length > 0) return
		if ((headRetries.get(feed) ?? 0) >= 6) emit(feed, { error, isLoading: false })
		else scheduleHeadRetry(feed)
	}
}

function stopPolling(feed: Feed) {
	if (feed.pollTimer !== null) {
		window.clearInterval(feed.pollTimer)
		feed.pollTimer = null
	}
}

function startPolling(feed: Feed) {
	if (feed.pollTimer !== null) return
	emit(feed, { transport: 'polling' })
	feed.pollTimer = window.setInterval(() => void fetchHead(feed), POLL_MS)
}

function closeSource(feed: Feed) {
	feed.source?.close()
	feed.source = null
	if (feed.reconnectTimer !== null) {
		window.clearTimeout(feed.reconnectTimer)
		feed.reconnectTimer = null
	}
}

function openSource(feed: Feed) {
	if (typeof EventSource === 'undefined') {
		startPolling(feed)
		return
	}
	closeSource(feed)
	const es = new EventSource(`${API_URL}/${feed.path}/stream`)
	feed.source = es
	es.addEventListener('snapshot', (e) => {
		feed.failures = 0
		feed.reconnectDelay = RECONNECT_MIN_MS
		stopPolling(feed)
		try {
			applyHead(feed, JSON.parse((e as MessageEvent).data))
		} catch {
			// A malformed snapshot is not fatal; deltas and polls still follow.
		}
		if (feed.state.transport !== 'live') emit(feed, { transport: 'live' })
	})
	es.addEventListener('delta', (e) => {
		let d: DeltaBody
		try {
			d = JSON.parse((e as MessageEvent).data)
		} catch {
			return
		}
		if (d.prev !== feed.state.version) {
			// Missed a change (or the head predates this stream): resync.
			void fetchHead(feed)
			return
		}
		emit(feed, { rows: applyDelta(feed.state.rows, d), version: d.version })
		markFresh(feed, d.added.map((r) => r.key))
	})
	es.onerror = () => {
		es.close()
		if (feed.source === es) feed.source = null
		feed.failures += 1
		if (feed.failures >= 2) startPolling(feed)
		feed.reconnectTimer = window.setTimeout(() => {
			feed.reconnectTimer = null
			if (feed.listeners.size > 0) openSource(feed)
		}, feed.reconnectDelay)
		feed.reconnectDelay = Math.min(feed.reconnectDelay * 2, RECONNECT_MAX_MS)
	}
}

function feedFor(path: string): Feed {
	let feed = feeds.get(path)
	if (!feed) {
		feed = {
			path,
			state: EMPTY,
			listeners: new Set(),
			next: null,
			source: null,
			reconnectDelay: RECONNECT_MIN_MS,
			reconnectTimer: null,
			pollTimer: null,
			releaseTimer: null,
			freshTimer: null,
			freshAt: new Map(),
			failures: 0
		}
		feeds.set(path, feed)
	}
	return feed
}

export function subscribe(path: string, listener: () => void): () => void {
	const feed = feedFor(path)
	feed.listeners.add(listener)
	if (feed.releaseTimer !== null) {
		window.clearTimeout(feed.releaseTimer)
		feed.releaseTimer = null
	}
	if (feed.listeners.size === 1 && !feed.source && feed.pollTimer === null) {
		// The GET is edge-cached and usually beats the stream's first frame;
		// the stream then keeps the list current.
		void fetchHead(feed)
		openSource(feed)
	}
	return () => {
		feed.listeners.delete(listener)
		if (feed.listeners.size > 0) return
		feed.releaseTimer = window.setTimeout(() => {
			feed.releaseTimer = null
			if (feed.listeners.size > 0) return
			closeSource(feed)
			stopPolling(feed)
			if (feed.freshTimer !== null) window.clearInterval(feed.freshTimer)
			feeds.delete(path)
		}, RELEASE_GRACE_MS)
	}
}

export function getSnapshot(path: string): FeedState {
	return feeds.get(path)?.state ?? EMPTY
}

export function getServerSnapshot(): FeedState {
	return EMPTY
}

/** Page back through history; rows append in the server's order. A page is
 * complete or it fails (503 while a source is slow); failures retry. */
export async function fetchMore(path: string, attempt = 0): Promise<void> {
	const feed = feeds.get(path)
	if (!feed || !feed.next || feed.state.isLoadingMore) return
	emit(feed, { isLoadingMore: true })
	try {
		const res = await fetch(`${API_URL}/${path}?cursor=${encodeURIComponent(feed.next)}`)
		if (!res.ok) throw new Error(`feed page ${res.status}`)
		const body: HeadBody = await res.json()
		const have = new Set(feed.state.rows.map((r) => r.key))
		const page = (body.rows ?? []).filter((r) => !have.has(r.key))
		feed.next = body.next ?? null
		emit(feed, {
			rows: [...feed.state.rows, ...page],
			hasMore: feed.next !== null,
			isLoadingMore: false
		})
	} catch (error) {
		emit(feed, { isLoadingMore: false, error: feed.state.rows.length === 0 ? error : null })
		if (attempt < 4 && feeds.get(path) === feed) {
			window.setTimeout(() => void fetchMore(path, attempt + 1), 1_000 * 2 ** attempt)
		}
	}
}

/** Feed path for a collection, or the market-wide feed. */
export function feedPath(slug?: string | null): string {
	return slug ? `collection/${encodeURIComponent(slug)}/feed` : 'inscriptions/activity/feed'
}
